import { useState } from 'react';
import { Link } from 'react-router-dom';

import { Button } from '@components/button';
import { Card } from '@components/card';
import { useCreateFormStore } from '@components/form';
import { createForm } from '@components/form/create-form';
import { IconButton } from '@components/icon-button';
import { IconFormIndicatorError } from '@components/icons/form-indicator-error';
import { IconFormIndicatorPending } from '@components/icons/form-indicator-pending';
import { IconFormIndicatorSuccess } from '@components/icons/form-indicator-success';
import { IconTrash } from '@components/icons/trash';
import { IconVisibilityShown } from '@components/icons/visibility-shown';
import { Spinner } from '@components/loading-spinner';
import { SelectOption } from '@components/select';
import { Separator } from '@components/separator';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { disabledSectionStyle } from '@styles/theme';
import { abbreviateDate } from '@utils/date';

import { IntegrationHeader } from 'components/IntegrationHeader';
import { useAuthenticatedUser } from 'features/authentication';
import { useModal } from 'features/modals';
import { applicationOnboardingVerifyAppUrl } from 'features/routing/urls';
import { useStore } from 'models/Provider';

import {
  Contract,
  Cosigner,
  ExternalListBank,
  ExternalListCostLocation,
  ExternalListJobTitles,
  ExternalListPension,
  ExternalListPostCode,
  ExternalListUnions,
  ExternalListWorkplace,
  Signer,
  SubmitDocumentForm,
  Template,
} from './api';
import {
  useDeleteReykjavikDocument,
  usePreviewDocument,
  useReykjavikExternalListBanks,
  useReykjavikExternalListCostLocations,
  useReykjavikExternalListJob,
  useReykjavikExternalListJobTitles,
  useReykjavikExternalListPensions,
  useReykjavikExternalListPostCodes,
  useReykjavikExternalListUnions,
  useReykjavikExternalListWorkplace,
  useReykjavikForm,
  useSubmitReykjavikDocument,
} from './hooks';
import locale from './locale.json';

type TemplateFormProps = {
  defaultValues: { [key: string]: string };
  templateId: string;
  applicationId: string;
  cosigners: Cosigner[];
  departments: SelectOption[];
  jobDepartment: string;
  jobLocation: string;
  isSsnValid: boolean;
};

const Form = createForm<SubmitDocumentForm>();

const TemplateForm = (props: TemplateFormProps) => {
  const { t } = useScopedTranslate(locale);
  const formStore = useCreateFormStore();
  const department = formStore.useFieldValue('department') as string;
  const location = formStore.useFieldValue('vinnustadur') as string;
  const job = formStore.useFieldValue('starfsheiti') as string;
  const postalCode = formStore.useFieldValue('postalcode') as string;
  const isTemporaryRecruitment = (
    (formStore.useFieldValue('radningartegund') ?? '') as string
  ).startsWith('Tímabundin ráðning');

  const workplaceQuery = useReykjavikExternalListWorkplace(department);
  const workplaceOptions: SelectOption[] = (workplaceQuery.data ?? []).map(
    (location: ExternalListWorkplace) => ({
      value: location.orgUnitId,
      label: location.name,
    })
  );
  const defaultWorkplace = workplaceOptions.find(
    (workplace) => workplace.value === props.jobLocation
  )?.value;

  const costLocationQuery = useReykjavikExternalListCostLocations(location);
  const costLocationOptions: SelectOption[] = (costLocationQuery.data ?? []).map(
    (location: ExternalListCostLocation) => ({
      value: `${location.costCenterCode} ${location.projectCode}`,
      label: `${location.costCenterName} ${location.costCenterCode} / ${location.projectName} ${location.projectCode}`,
    })
  );

  const jobTitlesQuery = useReykjavikExternalListJobTitles(department);
  const jobTitleOptions: SelectOption[] = (jobTitlesQuery.data ?? []).map(
    (title: ExternalListJobTitles) => ({
      value: String(title.id),
      label: `${title.code} ${title.name}`,
    })
  );

  const jobDetailQuery = useReykjavikExternalListJob(job);

  const unionsQuery = useReykjavikExternalListUnions(jobDetailQuery.data?.unionCode ?? '');
  const unionsOptions: SelectOption[] = (unionsQuery.data ?? []).map(
    (union: ExternalListUnions) => ({
      value: String(union.number),
      label: union.name,
    })
  );

  const pensionQuery = useReykjavikExternalListPensions(jobDetailQuery.data?.unionAgreement ?? '');
  const pensionOptions: SelectOption[] = (pensionQuery.data ?? []).map(
    (pension: ExternalListPension) => ({
      value: pension.id,
      label: pension.name,
    })
  );

  const banksQuery = useReykjavikExternalListBanks();
  const bankOptions: SelectOption[] = (banksQuery.data ?? []).map((bank: ExternalListBank) => ({
    value: bank.code,
    label: `${bank.name} - ${bank.code}`,
  }));

  const postCodesQuery = useReykjavikExternalListPostCodes();
  const postCodesOptions: SelectOption[] = (postCodesQuery.data ?? []).map(
    (postCode: ExternalListPostCode) => ({
      value: postCode.postalCode,
      label: `${postCode.postalCode} - ${postCode.town}`,
    })
  );

  const cosignerList = props.cosigners.map((cosigner) => ({
    value: cosigner.id.toString(),
    label: cosigner.name,
  }));

  const recruitmentType = [
    'Ótímabundin ráðning',
    'Tímabundin ráðning',
    'Tímabundin ráðning - Vegna sumarafleysinga',
    'Tímabundin ráðning - Vegna sumarvinnu',
  ];
  const workArrangementType = ['Mánaðarlaun', 'Tímavinna'];
  const ledgerType = ['05', '12', '13', '14', '15', '22', '26', '29', '64', '74'];

  const preview = usePreviewDocument(props.applicationId, props.templateId);
  const submit = useSubmitReykjavikDocument(props.applicationId, props.templateId);

  const submitErrors = submit.error?.response?.data.errors ?? [];

  const mapValues = (values: SubmitDocumentForm) => {
    const mappedValues = { ...values } as SubmitDocumentForm;
    mappedValues['banki'] = banksQuery.data?.find((bank) => bank.code === values['banki']);
    mappedValues['postalcode'] = postCodesQuery.data?.find(
      (code) => (code.postalCode ?? '').toString() === values['postalcode']
    );
    mappedValues['vinnustadur'] = workplaceQuery.data?.find(
      (location) => (location.orgUnitId ?? '').toString() === values['vinnustadur']
    );
    mappedValues['kostnadarstadur'] = costLocationQuery.data?.find(
      (location) =>
        `${location.costCenterCode} ${location.projectCode}`.toString() ===
        values['kostnadarstadur']
    );
    mappedValues['starfsheiti'] = jobTitlesQuery.data?.find(
      (title) => String(title.id) === values['starfsheiti']
    );
    mappedValues['stettarfelag'] = unionsQuery.data?.find((union) =>
      String(union.number === values['stettarfelag'])
    );
    mappedValues['lifeyrissjodur'] = pensionQuery.data?.find(
      (pension) => String(pension.id) === values['lifeyrissjodur']
    );
    mappedValues['kjarasamningur'] = jobDetailQuery.data?.unionAgreement ?? '';
    return mappedValues;
  };

  return (
    <Stack space={4}>
      <Form
        store={formStore}
        onSubmit={(values) => {
          return submit.mutateAsync(mapValues(values));
        }}
      >
        <Form.Select
          id="cosigners"
          options={cosignerList}
          label={t('selectCosigners')}
          placeholder={t('cosigners')}
          noOptions={() => t('noMoreCosigners')}
          validators={['required']}
        />
        <Text font="regular12" text={t('cosignerMissing')} />
        <Separator />
        <Text font="bold18" color="purple200" text={t('workplace')} />
        <Form.Select
          id="department"
          options={props.departments}
          defaultValue={props.jobDepartment}
          label={t('department')}
          isClearable={false}
          validators={['required']}
        />
        <Form.Select
          id="vinnustadur"
          options={workplaceOptions}
          label={t('workplace')}
          validators={['required']}
          defaultValue={defaultWorkplace}
          isDisabled={!department}
          isClearable={false}
          key={`workplace-${department}-${defaultWorkplace}`}
        />
        <Form.Select
          id="kostnadarstadur"
          options={costLocationOptions}
          label={t('costLocation')}
          validators={['required']}
          isDisabled={!location}
          key={`costLocation-${location}-${costLocationOptions.length}`}
          isClearable={false}
          defaultValue={costLocationOptions.length === 1 ? costLocationOptions[0]?.value : ''}
        />
        <Separator />
        <Text font="bold18" color="purple200" text={t('personalInfo')} />
        <Form.TextField
          id="name"
          label={t('applicantFullName')}
          validators={['required']}
          defaultValue={props.defaultValues?.name}
        />
        <Form.TextField
          id="ssn"
          label={t('applicantSSN')}
          validators={['required']}
          defaultValue={props.defaultValues?.ssn}
        />
        <Form.TextField
          id="email"
          label={t('email')}
          validators={['required', 'email']}
          defaultValue={props.defaultValues?.email}
        />
        <Form.PhoneField
          id="phone"
          label={t('phone')}
          validators={['required', 'phone']}
          defaultValue={props.defaultValues?.phone}
        />
        <Form.TextField
          id="address"
          label={t('legalResidence')}
          validators={['required']}
          defaultValue={props.defaultValues?.address}
        />
        <Form.Select
          id="postalcode"
          options={postCodesOptions}
          label={t('postalCode')}
          validators={['required']}
          isClearable={false}
          defaultValue={props.defaultValues?.postalCode}
        />
        <Form.TextField
          id="city"
          label={t('city')}
          validators={['required']}
          key={postalCode}
          defaultValue={
            postCodesQuery.data?.find((code) => (code.postalCode ?? '').toString() === postalCode)
              ?.town
          }
        />
        <Separator />
        <Text font="bold18" color="purple200" text={t('jobInfo')} />
        <Form.Select
          id="starfsheiti"
          options={jobTitleOptions}
          label={t('jobTitle')}
          validators={['required']}
          isClearable={false}
          isDisabled={!location}
          key={`jobTitle-${location}`}
        />
        <Form.TextField
          id="grunnlaunaflokkur"
          label={t('basicWageGroup')}
          isDisabled={true}
          key={job}
          defaultValue={jobDetailQuery.data?.basicWageGroup}
          validators={['required']}
        />
        <Form.Select
          id="stettarfelag"
          options={unionsOptions}
          label={t('union')}
          validators={['required']}
          isClearable={false}
          isDisabled={!job}
          key={`union-${job}-${unionsOptions.length}`}
          defaultValue={unionsOptions[0]?.value ?? ''}
        />
        <Form.Select
          id="lifeyrissjodur"
          options={pensionOptions}
          label={t('pension')}
          validators={['required']}
          isClearable={false}
          isDisabled={!job}
          key={`pension-${job}`}
          defaultValue={pensionOptions[0]?.value ?? ''}
        />
        <Form.TextField
          id="starfshlutfall"
          label={t('jobPercentage')}
          validators={['required', 'decimal']}
        />
        <Form.Select
          id="vinnufyrirkomulag"
          label={t('workArrangement')}
          validators={['required']}
          options={workArrangementType.map((type) => ({ value: type, label: type }))}
          isClearable={false}
        />
        <Form.Select
          id="vaktavinna"
          label={t('shiftWork')}
          options={[
            { value: '1', label: t('yes') },
            { value: '0', label: t('no') },
          ]}
          validators={['required']}
          isClearable={false}
        />
        <Separator />
        <Text font="bold18" color="purple200" text={t('recruitmentInfo')} />
        <Form.Select
          id="radningartegund"
          label={t('recruitmentType')}
          validators={['required']}
          options={recruitmentType.map((type) => ({ value: type, label: type }))}
          isClearable={false}
        />
        <Form.Select
          id="odru_starfi"
          label={t('employeeOtherJob')}
          options={[
            { value: '1', label: t('yes') },
            { value: '0', label: t('no') },
          ]}
          validators={['required']}
          isClearable={false}
          defaultValue={['Yes', 'Já'].includes(props.defaultValues?.inAnotherJob) ? '1' : '0'}
        />
        <Form.DatePicker
          id="first_workday_date"
          label={t('firstWorkday')}
          validators={['required']}
        />
        <Form.DatePicker
          id="last_workday_date"
          label={t('lastWorkday')}
          validators={isTemporaryRecruitment ? ['required'] : []}
          key={`temporary-${String(isTemporaryRecruitment)}`}
        />
        <Form.TextField
          id="personuafslattur_hlutfall"
          label={t('personalTaxAllowance')}
          validators={['decimal', 'required']}
          defaultValue={
            ['Yes', 'Já'].includes(props.defaultValues?.personalExemption) ? '100' : '0'
          }
        />
        <Separator />
        <Text font="bold18" color="purple200" text={t('overtime')} />
        <Form.Select
          id="m_yv"
          label={t('measuredOvertime')}
          options={[
            { value: '1', label: t('yes') },
            { value: '0', label: t('no') },
          ]}
        />
        <Form.Select
          id="f_yv"
          label={t('fixedOvertime')}
          options={[
            { value: '1', label: t('yes') },
            { value: '0', label: t('no') },
          ]}
        />
        <Form.Textarea id="annad" label={t('otherOvertime')} validators={[]} />
        <Separator />
        <Text font="bold18" color="purple200" text={t('salaryInfo')} />
        <Form.Select
          id="banki"
          options={bankOptions}
          label={t('bank')}
          validators={['required']}
          defaultValue={props.defaultValues?.banki}
          isClearable={false}
        />
        <Form.Select
          id="hb"
          options={ledgerType.map((type) => ({ value: type, label: type }))}
          label={t('ledger')}
          validators={['required']}
          defaultValue={props.defaultValues?.hb ?? '26'}
          isClearable={false}
        />
        <Form.TextField
          id="rnr"
          label={t('accountNumber')}
          validators={['required', 'account-number']}
          defaultValue={props.defaultValues?.rnr}
        />
        <Form.Actions>
          {preview.isLoading && <Spinner />}
          <Button
            isDisabled={preview.isLoading || submit.isLoading}
            tooltip={t('preview')}
            iconRight={<IconVisibilityShown />}
            variant="ghost"
            onClick={() => {
              preview.mutateAsync(mapValues(formStore.getState().values as SubmitDocumentForm));
            }}
          />

          <Form.Submit text={t('send')} disabled={!props.isSsnValid} />
        </Form.Actions>
      </Form>
      {submitErrors.length > 0 && (
        <Card variant="red" space={4}>
          <Stack space={2}>
            <Text font="bold14" text={t('returnedErrors')} />
            {submitErrors.map((error, idx) => (
              <Text font="regular14" text={error} key={idx} />
            ))}
          </Stack>
        </Card>
      )}
    </Stack>
  );
};

type NewFormProps = {
  applicationId: string;
  templates: Template[];
  cosigners: Cosigner[];
  defaultValues: { [key: string]: string };
  departments: SelectOption[];
  jobId: number;
  jobDepartment: string;
  jobLocation: string;
  isSsnValid: boolean;
};

const NewForm = (props: NewFormProps) => {
  const { t } = useScopedTranslate(locale);
  const [templateId, setTemplateId] = useState<string | null>(null);

  const templateList = props.templates.map((template) => ({
    value: template.id.toString(),
    label: template.title,
  }));
  if (!templateId && templateList.length > 0) {
    setTemplateId(templateList[0].value);
  }

  const verifyUrl = applicationOnboardingVerifyAppUrl(parseInt(props.applicationId), props.jobId);

  return (
    <Stack space={4}>
      {!props.isSsnValid && (
        <Card space={4} variant="orange">
          <Stack space={2}>
            <Text font="regular16" color="orange200" text={t('invalidSSN')} />
            <Link to={verifyUrl}>
              <Text font="regular14" color="orange200" text={t('verifySSN')} />
            </Link>
          </Stack>
        </Card>
      )}
      <Card variant="transparent" space={4}>
        <Stack space={4}>
          {templateId ? (
            <TemplateForm
              defaultValues={props.defaultValues}
              templateId={templateId}
              applicationId={props.applicationId}
              cosigners={props.cosigners}
              departments={props.departments}
              jobDepartment={props.jobDepartment}
              jobLocation={props.jobLocation}
              isSsnValid={props.isSsnValid}
            />
          ) : (
            <div>{t('noTemplate')}</div>
          )}
        </Stack>
      </Card>
    </Stack>
  );
};

type SignersProps = {
  signers: Signer[];
};

const Signers = (props: SignersProps) => {
  const { t } = useScopedTranslate(locale);
  return (
    <>
      {props.signers.map((signer) => {
        return (
          <Stack space={1} key={signer.name}>
            <Text color="purple100" font="semibold14" text={t('signer', signer.name)} />
            <Stack space={1} orientation="horizontal">
              {signer.status === 'signed' ? (
                <IconFormIndicatorSuccess />
              ) : signer.status === 'pending' ? (
                <IconFormIndicatorPending />
              ) : (
                <IconFormIndicatorError />
              )}
              <Text
                color={
                  signer.status === 'signed'
                    ? 'green100'
                    : signer.status === 'pending'
                    ? 'orange100'
                    : 'red100'
                }
                text={
                  signer.status === 'signed'
                    ? t('signed')
                    : signer.status === 'pending'
                    ? t('pending')
                    : t('declined')
                }
              />
            </Stack>
          </Stack>
        );
      })}
    </>
  );
};

type ContractsProps = {
  contracts: Contract[];
  applicationId: string;
};

const Contracts = (props: ContractsProps) => {
  const { t } = useScopedTranslate(locale);
  const { lang } = useStore();
  const { showFullscreenModal } = useModal();
  const deleteDocument = useDeleteReykjavikDocument(props.applicationId);
  const me = useAuthenticatedUser();

  return (
    <>
      {props.contracts.map((contract) => (
        <Card
          style={deleteDocument.isLoading ? disabledSectionStyle : {}}
          space={4}
          key={contract.id}
        >
          <Stack space={4}>
            <Stack space={1}>
              <Text color="purple100" font="semibold14" text={t('document')} />
              <Text text={contract.templateTitle} />
            </Stack>
            <Stack space={1}>
              <Text color="purple100" font="semibold14" text={t('dateSent')} />
              <Text text={abbreviateDate(contract.dateSent, lang, 'datetime')} />
            </Stack>
            <Stack space={1}>
              <Text color="purple100" font="semibold14" text={t('sentBy')} />
              <Text text={contract.senderName} />
            </Stack>
            <Signers signers={contract.signers} />
            <Stack space={2} orientation="horizontal" justify={'flex-end'}>
              <IconButton
                icon={<IconVisibilityShown />}
                tooltip={t('preview')}
                onClick={() =>
                  showFullscreenModal('CUSTOM_FILE_PREVIEW', { filePath: contract.url })
                }
              />
              {contract.senderId === me.id && (
                <IconButton
                  icon={<IconTrash />}
                  tooltip={t('delete')}
                  onClick={() => deleteDocument.mutate(contract.id)}
                />
              )}
            </Stack>
          </Stack>
        </Card>
      ))}
    </>
  );
};

export const ReykjavikOnboarding = (props: { applicationId: string }) => {
  const { applicationId } = props;

  const formQuery = useReykjavikForm(applicationId);

  if (formQuery.isError) return null;
  if (!formQuery.isSuccess) return <Spinner />;

  const templates = formQuery.data.templates;
  const cosigners = formQuery.data.cosigners;
  const defaultValues = formQuery.data.defaultValues;
  const contracts = formQuery.data.contracts;
  const departments = formQuery.data.departments;
  const job = formQuery.data.job;

  return (
    <Stack space={8}>
      <IntegrationHeader
        integration={{
          name: formQuery.data.name,
          title: formQuery.data.title,
          description: formQuery.data.description,
        }}
      />
      <Contracts applicationId={props.applicationId} contracts={contracts} />
      {contracts.length === 0 ? (
        <NewForm
          applicationId={applicationId}
          templates={templates}
          cosigners={cosigners}
          defaultValues={defaultValues}
          departments={departments}
          jobId={job.id}
          jobDepartment={job.department}
          jobLocation={job.location}
          isSsnValid={formQuery.data.isSsnValid}
        />
      ) : null}
    </Stack>
  );
};
